import React, { useRef, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Btn, Spinner } from '../UI';
import * as api from '../../api';

// Template no longer includes departments — mapped in UI
const TEMPLATE_COLS = ['Code', 'Name', 'Unit of Measure', 'Category', 'Standard Rate', 'Lead Time (Days)', 'Description'];
const COL_MAP = {
  code: ['code', 'mat code', 'material code'],
  name: ['name', 'material name', 'item name'],
  unit_of_measure: ['unit', 'unit of measure', 'uom'],
  category: ['category', 'cat'],
  standard_rate: ['standard rate', 'rate', 'std rate', 'std. rate'],
  standard_lead_time_days: ['lead time', 'lead time (days)', 'lead days'],
  description: ['description', 'desc'],
};

export function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    TEMPLATE_COLS,
    ['', 'Example Material', 'PCS', 'ACCESSORIES', '1500', '3', 'Optional description'],
  ]);
  const headers = ['Code', 'Name', 'Unit of Measure', 'Category', 'Standard Rate', 'Lead Time (Days)', 'Description'];
  ws['!cols'] = [8, 28, 16, 16, 14, 16, 36].map(w => ({ wch: w }));

  // Style header row
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let C = range.s.c; C <= range.e.c; C++) {
    for (let R = range.s.r; R <= range.e.r; R++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[addr]) ws[addr] = { v: '', t: 's' };
      ws[addr].s = {
        font: { bold: R === 0, color: { rgb: R === 0 ? 'FFFFFF' : '1E293B' }, name: 'Calibri', sz: 10 },
        fill: { fgColor: { rgb: R === 0 ? '0B318B' : 'FFFFFF' }, patternType: 'solid' },
        alignment: { vertical: 'center', horizontal: 'left' },
        border: {
          top: { style: 'thin', color: { rgb: 'CBD5E1' } }, bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
          left: { style: 'thin', color: { rgb: 'CBD5E1' } }, right: { style: 'thin', color: { rgb: 'CBD5E1' } },
        },
      };
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Materials');
  XLSX.writeFile(wb, 'materials_import_template.xlsx');
}

function parseSheet(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  if (raw.length < 2) return [];

  const headers = raw[0].map(h => String(h).trim());
  const colIdx = {};
  headers.forEach((h, i) => {
    const hl = h.toLowerCase();
    for (const [field, aliases] of Object.entries(COL_MAP)) {
      if (aliases.includes(hl) && !(field in colIdx)) colIdx[field] = i;
    }
  });

  return raw.slice(1).map((r, i) => {
    const get = field => colIdx[field] != null ? String(r[colIdx[field]] ?? '').trim() : '';
    return {
      _key: i,
      code: get('code'),
      name: get('name'),
      unit_of_measure: get('unit_of_measure'),
      category: get('category'),
      standard_rate: get('standard_rate'),
      standard_lead_time_days: get('standard_lead_time_days'),
      description: get('description'),
    };
  }).filter(r => r.name);
}

const CELL = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12, whiteSpace: 'nowrap' };
const HEAD = { ...CELL, background: '#0b318b', color: '#fff', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 };

// Per-row department selector — uses position:fixed so it escapes overflow:auto containers
function DeptSelector({ depts, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef();
  const dropRef = useRef();

  const openDropdown = () => {
    if (disabled) return;
    const rect = btnRef.current.getBoundingClientRect();
    // flip up if not enough room below
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropH = Math.min(260, depts.length * 37 + 8);
    const top = spaceBelow >= dropH ? rect.bottom + 4 : rect.top - dropH - 4;
    setPos({ top, left: rect.left, width: Math.max(rect.width, 260) });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropRef.current && !dropRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const names = depts.filter(d => selected.has(d.id || d.department_id)).map(d => d.name);

  return (
    <div style={{ position: 'relative', minWidth: 140 }}>
      <button
        ref={btnRef}
        onClick={openDropdown}
        style={{
          width: '100%', textAlign: 'left', background: disabled ? '#f8fafc' : '#fff',
          border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px',
          cursor: disabled ? 'default' : 'pointer',
          fontSize: 11, color: names.length ? '#0b318b' : '#94a3b8',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {names.length ? names.join(', ') : 'Select departments…'}
        </span>
        {!disabled && <span style={{ flexShrink: 0, color: '#94a3b8' }}>▾</span>}
      </button>

      {open && (
        <div
          ref={dropRef}
          style={{
            position: 'fixed', zIndex: 99999,
            top: pos.top, left: pos.left, width: pos.width,
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            maxHeight: 260, overflowY: 'auto',
          }}
        >
          {depts.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>No departments found.</div>
          )}
          {depts.map(dept => {
            const id = dept.id || dept.department_id;
            const checked = selected.has(id);
            return (
              <label
                key={id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                  cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  background: checked ? '#eff6ff' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = new Set(selected);
                    next.has(id) ? next.delete(id) : next.add(id);
                    onChange(next);
                  }}
                  style={{ width: 14, height: 14, accentColor: '#0b318b', cursor: 'pointer', flexShrink: 0 }}
                />
                <span style={{ fontSize: 12.5, fontWeight: checked ? 600 : 400, color: '#1e293b' }}>{dept.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MaterialImportModal({ onClose, onImported, toast }) {
  const fileRef = useRef();
  const [rows, setRows] = useState(null);
  const [removed, setRemoved] = useState(new Set());
  const [phase, setPhase] = useState('preview');
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);

  // Per-row dept selections: { [_key]: Set<deptId> }
  const [rowDepts, setRowDepts] = useState({});
  const [allDepts, setAllDepts] = useState([]);

  // Load departments once on mount
  useEffect(() => {
    api.listDepartments(0, 500).then(res => {
      const list = Array.isArray(res) ? res : (res?.departments || []);
      setAllDepts(list);
    }).catch(() => {});
  }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const wb = XLSX.read(ev.target.result, { type: 'array' });
      const parsed = parseSheet(wb);
      if (!parsed.length) { toast.show('No data rows found in the file.', 'error'); return; }
      setRows(parsed);
      setRemoved(new Set());
      // init empty dept sets
      const init = {};
      parsed.forEach(r => { init[r._key] = new Set(); });
      setRowDepts(init);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const toggleRemove = (key) => setRemoved(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });

  const setRowDeptSelection = (key, selection) => setRowDepts(prev => ({ ...prev, [key]: selection }));

  // Apply dept selection to all active rows at once
  const applyToAll = (selection) => {
    if (!rows) return;
    const next = {};
    rows.forEach(r => { next[r._key] = new Set(selection); });
    setRowDepts(next);
  };
  const [globalDepts, setGlobalDepts] = useState(new Set());

  const activeRows = rows ? rows.filter(r => !removed.has(r._key)) : [];

  const runImport = async () => {
    if (!activeRows.length) return;
    setPhase('importing');
    setProgress(0);

    const BATCH = 10;
    const allResults = [];
    let created = 0, skipped = 0, errors = 0;

    for (let i = 0; i < activeRows.length; i += BATCH) {
      const chunk = activeRows.slice(i, i + BATCH).map(r => {
        const deptIds = rowDepts[r._key] || new Set();
        const deptNames = allDepts
          .filter(d => deptIds.has(d.id || d.department_id))
          .map(d => d.name)
          .join(',');
        return {
          code: r.code || null,
          name: r.name,
          unit_of_measure: r.unit_of_measure,
          category: r.category || null,
          standard_rate: r.standard_rate ? parseFloat(r.standard_rate) : null,
          standard_lead_time_days: r.standard_lead_time_days ? parseInt(r.standard_lead_time_days, 10) : null,
          description: r.description || null,
          departments: deptNames || null,
        };
      });

      try {
        const res = await api.bulkImportMaterials(chunk);
        allResults.push(...res.results);
        created += res.created; skipped += res.skipped; errors += res.errors;
      } catch (err) {
        chunk.forEach((c, ci) => allResults.push({ row: i + ci + 2, name: c.name, status: 'error', message: err.message }));
        errors += chunk.length;
      }
      setProgress(Math.min(100, Math.round(((i + BATCH) / activeRows.length) * 100)));
    }

    setSummary({ total: activeRows.length, created, skipped, errors, results: allResults });
    setPhase('done');
    if (created > 0) onImported();
  };

  return (
    <Modal
      title="Import Materials from Excel"
      onClose={() => phase !== 'importing' && onClose()}
      width={1100}
    >
      {phase !== 'done' && (
        <>
          {/* Top bar */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <Btn variant="ghost" onClick={downloadTemplate} style={{ fontSize: 13 }}>⬇ Download Template</Btn>
            <span style={{ color: '#cbd5e1', fontSize: 16 }}>|</span>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFile} />
            <Btn onClick={() => fileRef.current.click()} style={{ fontSize: 13 }}>
              📂 {rows ? 'Change File' : 'Select Excel File'}
            </Btn>
            {rows && (
              <span style={{ fontSize: 13, color: '#64748b', marginLeft: 4 }}>
                {rows.length} row{rows.length !== 1 ? 's' : ''} · {removed.size} removed · <strong>{activeRows.length}</strong> to import
              </span>
            )}
          </div>

          {/* Empty state */}
          {!rows && (
            <div style={{ border: '2px dashed #cbd5e1', borderRadius: 10, padding: '40px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
              <div>Select an Excel file (.xlsx / .xls) to preview before importing.</div>
              <div style={{ marginTop: 6, fontSize: 12.5 }}>
                Department mapping is done here — no departments column needed in the file.{' '}
                <button onClick={downloadTemplate} style={{ background: 'none', border: 'none', color: '#0b318b', fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 12.5 }}>
                  Download template ⬇
                </button>
              </div>
            </div>
          )}

          {/* Preview table */}
          {rows && (
            <>
              {/* Apply-to-all department selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '8px 12px', background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#3730a3', whiteSpace: 'nowrap' }}>Apply departments to all rows:</span>
                <div style={{ flex: 1, maxWidth: 340 }}>
                  <DeptSelector
                    depts={allDepts}
                    selected={globalDepts}
                    onChange={(sel) => { setGlobalDepts(sel); applyToAll(sel); }}
                  />
                </div>
                <span style={{ fontSize: 11, color: '#64748b' }}>— or set per row individually below</span>
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 420 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ ...HEAD, width: 36 }}></th>
                      <th style={HEAD}>Code</th>
                      <th style={HEAD}>Name</th>
                      <th style={HEAD}>Unit</th>
                      <th style={HEAD}>Category</th>
                      <th style={HEAD}>Std Rate</th>
                      <th style={HEAD}>Lead Days</th>
                      <th style={HEAD}>Description</th>
                      <th style={{ ...HEAD, minWidth: 200 }}>Departments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const isRemoved = removed.has(r._key);
                      return (
                        <tr key={r._key} style={{ opacity: isRemoved ? 0.35 : 1, background: isRemoved ? '#fef2f2' : 'inherit' }}>
                          <td style={{ ...CELL, textAlign: 'center' }}>
                            <button
                              title={isRemoved ? 'Restore row' : 'Remove row'}
                              onClick={() => toggleRemove(r._key)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: isRemoved ? '#16a34a' : '#dc2626', padding: '0 2px' }}
                            >{isRemoved ? '↩' : '✕'}</button>
                          </td>
                          <td style={{ ...CELL, fontFamily: 'monospace', color: '#475569' }}>{r.code || <span style={{ color: '#94a3b8' }}>auto</span>}</td>
                          <td style={{ ...CELL, fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</td>
                          <td style={CELL}>{r.unit_of_measure}</td>
                          <td style={CELL}>{r.category || '—'}</td>
                          <td style={{ ...CELL, textAlign: 'right' }}>{r.standard_rate || '—'}</td>
                          <td style={{ ...CELL, textAlign: 'center' }}>{r.standard_lead_time_days || '—'}</td>
                          <td style={{ ...CELL, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description || '—'}</td>
                          <td style={{ ...CELL, padding: '4px 8px' }}>
                            <DeptSelector
                              depts={allDepts}
                              selected={rowDepts[r._key] || new Set()}
                              onChange={(sel) => setRowDeptSelection(r._key, sel)}
                              disabled={isRemoved}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {phase === 'importing' && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    <span>Importing…</span><span>{progress}%</span>
                  </div>
                  <div style={{ height: 10, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: '#0b318b', borderRadius: 99, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <Btn variant="ghost" onClick={onClose} disabled={phase === 'importing'}>Cancel</Btn>
                <Btn
                  onClick={runImport}
                  disabled={!activeRows.length || phase === 'importing'}
                  style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {phase === 'importing' && <Spinner size={14} />}
                  Import {activeRows.length} Material{activeRows.length !== 1 ? 's' : ''}
                </Btn>
              </div>
            </>
          )}
        </>
      )}

      {/* Summary */}
      {phase === 'done' && summary && (
        <>
          <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
            {[
              { label: 'Total',   value: summary.total,   color: '#1e293b', bg: '#f8fafc' },
              { label: 'Created', value: summary.created, color: '#15803d', bg: '#dcfce7' },
              { label: 'Skipped', value: summary.skipped, color: '#b45309', bg: '#fef3c7' },
              { label: 'Errors',  value: summary.errors,  color: '#dc2626', bg: '#fee2e2' },
            ].map(s => (
              <div key={s.label} style={{ flex: 1, background: s.bg, borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 360 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={HEAD}>Row</th>
                  <th style={HEAD}>Code</th>
                  <th style={HEAD}>Name</th>
                  <th style={HEAD}>Status</th>
                  <th style={HEAD}>Message</th>
                </tr>
              </thead>
              <tbody>
                {summary.results.map((r, i) => {
                  const sc = r.status === 'created' ? '#15803d' : r.status === 'skipped' ? '#b45309' : '#dc2626';
                  const sb = r.status === 'created' ? '#dcfce7' : r.status === 'skipped' ? '#fef3c7' : '#fee2e2';
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ ...CELL, textAlign: 'center', color: '#94a3b8' }}>{r.row}</td>
                      <td style={{ ...CELL, fontFamily: 'monospace', color: '#0b318b', fontWeight: 700 }}>{r.code || '—'}</td>
                      <td style={{ ...CELL, fontWeight: 600 }}>{r.name}</td>
                      <td style={{ ...CELL, textAlign: 'center' }}>
                        <span style={{ background: sb, color: sc, borderRadius: 12, padding: '2px 10px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ ...CELL, color: '#64748b' }}>{r.message || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn onClick={onClose} style={{ background: '#0b318b', borderColor: '#0b318b', color: '#fff' }}>Done</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}
